import type { RouteLocationNormalizedGeneric } from '#vue-router'
import type { UsersIndexData } from '../model/interfaces'
import type { ApiResponse } from '@shared/api'

import { getAxiosErrorMessage } from '@shared/api'
import { pick } from '@shared/lib/collections'
import { MAX_PER_PAGE, toPaginationValue, useAdminRoutes } from '@shared/lib/routes'
import { useToast } from '@shared/ui/toast'

import { useUsersStore } from '../model/store'

interface UsersApiOptions {
  route?: RouteLocationNormalizedGeneric
}

/**
 * `GET /admin-api/users` — the index. Returns `{ users, teams, meta }`; the `teams` block is the
 * full list used by the team filter and the edit form's team picker.
 */
export const useUsersApi = async (options?: UsersApiOptions): Promise<UsersIndexData | null> => {
  const { $axios } = useNuxtApp()
  const toast = useToast()
  const route = options?.route || useRoute()
  const store = useUsersStore()

  const url = useAdminRoutes('admin.users.data', pick({
    q: store.getFilterSearch,
    status: store.getFilterStatus,
    roles: store.getFilterRoles.length ? store.getFilterRoles : undefined,
    teams: store.getFilterTeams.length ? store.getFilterTeams : undefined,
    per_page: toPaginationValue(route.query?.per_page, store.getPerPage, MAX_PER_PAGE),
    page: toPaginationValue(route.query?.page, store.getCurrentPage),
  }))

  store.setLoading(true)

  try {
    if (!url) {
      toast.error('Unexpected error. Invalid request URL.')
      return null
    }

    const { data } = await $axios.get<ApiResponse>(url)

    if (!data.success) {
      toast.error(data.message)
      return null
    }

    const indexData: UsersIndexData | null = data.data || null

    if (indexData) {
      store.setUsers(indexData.users)
      store.setMeta(indexData.meta)
      store.setTeams(indexData.teams)
    }

    return indexData
  } catch (err: unknown) {
    toast.error(getAxiosErrorMessage(err))
    return null
  } finally {
    store.setLoading(false)
  }
}
